import { FormattedMessage, useIntl } from 'react-intl';
import { useRemoveMemberMutation } from 'src/api/mutations/userManagementMutation';
import { planUpgradeRequired } from 'src/api/utils/errorHandlers';
import { useAlerts } from 'src/common/AlertManager';
import { Button } from 'src/components';
import BaseModal from 'src/components/BaseModal';
import { BaseModalProps } from 'src/components/BaseModal/BaseModal';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useModal from 'src/hooks/useModal';

import styles from '../styles.module.scss';
import messages from '../messages';

interface RemoveMemberModalProps extends BaseModalProps {
  user: any;
  onRemoved: () => void;
}

/**
 * Confirm removing a member (or revoking a pending invite) — plan-structure v2
 * Phase 7a.
 *
 * The copy carries the whole point of the design: this is NOT a delete. The
 * account is kept so the person stays the author of everything they uploaded;
 * what actually happens is that access stops and the SEAT is freed. Admins
 * only reach for this when they are short of seats, so saying that plainly is
 * what makes it safe to click.
 */
const RemoveMemberModal = ({ user, onRemoved, ...rest }: RemoveMemberModalProps) => {
  const { formatMessage } = useIntl();
  const { showSuccess, showAlert } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const { closeModal } = useModal();

  const isPendingInvite = user?.invite_status === 'REQUESTED';

  const removeMutation = useRemoveMemberMutation(user?.id ?? -1, {
    onSuccess: () => {
      showSuccess(formatMessage(messages.removeMemberSuccess, { email: user?.email }));
      closeModal();
      onRemoved();
    },
    onError: (error: any) => {
      const gate = planUpgradeRequired(error);
      showAlert(gate ? (gate.message ?? '') : getErrorMessage(error));
    },
  });

  if (!user) return null;

  return (
    <BaseModal {...rest}>
      <h3>
        <FormattedMessage
          {...(isPendingInvite ? messages.revokeInviteTitle : messages.removeMemberTitle)}
          values={{ email: user.email }}
        />
      </h3>
      <p>
        <FormattedMessage
          {...(isPendingInvite ? messages.revokeInviteBody : messages.removeMemberBody)}
        />
      </p>
      <div className={styles.groupedButtons}>
        <Button
          mode="decline"
          size="small"
          onClick={() => removeMutation.mutate()}
          loading={removeMutation.isLoading}
        >
          <FormattedMessage {...(isPendingInvite ? messages.revokeInvite : messages.removeMember)} />
        </Button>
        <Button mode="tertiary" size="small" onClick={closeModal} disabled={removeMutation.isLoading}>
          <FormattedMessage {...messages.cancel} />
        </Button>
      </div>
    </BaseModal>
  );
};

export default RemoveMemberModal;
